import React, { useEffect, useRef } from 'react';
import ZFlixSplashLogo from './ZFlixSplashLogo';

const COLORS = ['#e50914', '#ff6b7a', '#f5c518', '#2ecc71', '#6b9fff', '#ff9f43'];

function spawnParticles(w, h, count) {
  const cx = w / 2;
  const cy = h / 2;
  const spread = Math.max(w, h) * 0.55;
  return Array.from({ length: count }, (_, i) => {
    const angle = (i / count) * Math.PI * 2 + Math.random() * 0.4;
    const dist = spread * (0.35 + Math.random() * 0.65);
    return {
      x: cx + Math.cos(angle) * dist,
      y: cy + Math.sin(angle) * dist,
      tx: cx + (Math.random() - 0.5) * 24,
      ty: cy + (Math.random() - 0.5) * 24,
      r: 2 + Math.random() * 4,
      color: COLORS[i % COLORS.length],
      delay: Math.random() * 0.15,
    };
  });
}

export default function SplashParticleBurst({ reducedMotion = false }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    if (reducedMotion) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.parentElement?.getBoundingClientRect();
      const w = rect?.width || window.innerWidth;
      const h = rect?.height || window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return { w, h };
    };

    let { w, h } = resize();
    let particles = spawnParticles(w, h, 72);
    const start = performance.now();
    const duration = 1100;

    const draw = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const ease = 1 - (1 - t) ** 3;
      ctx.clearRect(0, 0, w, h);

      particles.forEach((p) => {
        const localT = Math.max(0, Math.min(1, (t - p.delay) / (1 - p.delay)));
        const e = 1 - (1 - localT) ** 2.2;
        const x = p.x + (p.tx - p.x) * e;
        const y = p.y + (p.ty - p.y) * e;
        const alpha = localT < 0.85 ? 0.95 : 1 - (localT - 0.85) / 0.15;
        ctx.globalAlpha = alpha * (0.4 + ease * 0.6);
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(x, y, p.r * (0.6 + ease * 0.5), 0, Math.PI * 2);
        ctx.fill();
      });

      ctx.globalAlpha = 1;
      if (t < 1) {
        rafRef.current = requestAnimationFrame(draw);
      }
    };

    const onResize = () => {
      ({ w, h } = resize());
      particles = spawnParticles(w, h, 72);
    };
    window.addEventListener('resize', onResize);
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
    };
  }, [reducedMotion]);

  return (
    <div className="zflix-splash-stage zflix-splash-particles">
      {!reducedMotion && (
        <canvas
          ref={canvasRef}
          className="zflix-splash-particles-canvas"
          aria-hidden
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
        />
      )}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <ZFlixSplashLogo size="large" />
      </div>
    </div>
  );
}
